import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IntlTelInput from 'react-intl-tel-input';

import 'react-intl-tel-input/dist/main.css';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ParagraphNormal } from '../Paragraph';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import { shouldIgnoreAreaCodeHelper } from '../../util/helpers/phoneNumberHelpers';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';

const getBorderColors = (isFocused, hasError) => {
  if (isFocused) {
    return themeColors.colorSvgSemidark;
  }

  if (hasError) {
    return themeColors.colorErrorMessage;
  }

  return themeColors.colorBorderPrimary;
};

const PhoneFiledWrap = styled.div`
  > .intl-tel-input {
    width: 100%;

    .country-list {
      width: 100%;
    }

    input {
      width: 100%;
      padding: 12px;
      border-radius: ${variables.borderRadius.borderRadius};
      color: ${themeColors.colorTextSecondary};
      font-weight: 400;
      font-size: ${({ fontSize }) =>
        fontSize ?? variables.typography.p.small.fontSize};
      text-transform: ${({ hasSuggestion }) =>
        hasSuggestion ? 'capitalize' : 'none'};
      outline: none;
      border: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }

    .selected-flag {
      border-right: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }
  }
`;

const Label = styled(ParagraphNormal)`
  line-height: 2.1;
  font-weight: 600;
`;

export const ErrorMessage = styled(ParagraphNormal)`
  font-size: 0.8rem;
  margin-top: 2px;
  color: ${themeColors.colorErrorMessage};
`;

const PhoneField = ({
  setValue,
  name,
  label,
  errors,
  defaultValue,
  isIraAccount,
  isPhoneConnectedTo2FA,
  dataCy,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const error = errors[name];
  const selectedISOCodesListToIgnoreAreaCode = useSelector(
    selectPortalGatewayISOCodesListToIgnoreAreaCode,
  );

  const processNumber = (isValid, phone, country) => {
    const shouldIgnoreAreaCode = shouldIgnoreAreaCodeHelper(
      country,
      selectedISOCodesListToIgnoreAreaCode,
    );

    if (shouldIgnoreAreaCode) {
      if (country.iso2 === 'pr') {
        return `+${country.dialCode}${phone}`;
      }

      const phoneNumber = `+${country.dialCode}${phone.substring(3)}`;
      return phoneNumber;
    }

    if (!phone) {
      return '';
    }

    if (phone.includes(`+${country.dialCode}`)) {
      return `${phone}`;
    }

    return `+${country.dialCode}${phone}`;
  };

  return (
    <PhoneFiledWrap isFocused={isFocused} hasError={error} data-cy={dataCy}>
      {!!label && <Label>{label}</Label>}

      {isPhoneConnectedTo2FA ? (
        <IntlTelInput
          onPhoneNumberFocus={() => {
            setIsFocused(true);
          }}
          onPhoneNumberBlur={() => {
            setIsFocused(false);
          }}
          onPhoneNumberChange={(isValid, phone, country) => {
            setValue(name, processNumber(isValid, phone, country));
          }}
          fieldId={name}
          fieldName={name}
          defaultValue={defaultValue}
          onlyCountries={isIraAccount ? ['us'] : []}
          preferredCountries={isIraAccount ? [] : ['us']}
          defaultCountry="us"
          autoFocus
          disabled
          autocomplete="tel"
        />
      ) : (
        <IntlTelInput
          onPhoneNumberFocus={() => {
            setIsFocused(true);
          }}
          onPhoneNumberBlur={() => {
            setIsFocused(false);
          }}
          onPhoneNumberChange={(isValid, phone, country) => {
            setValue(name, processNumber(isValid, phone, country));
          }}
          fieldId={name}
          fieldName={name}
          defaultValue={defaultValue}
          onlyCountries={isIraAccount ? ['us'] : []}
          preferredCountries={isIraAccount ? [] : ['us']}
          defaultCountry="us"
          autoFocus
        />
      )}

      {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
    </PhoneFiledWrap>
  );
};

PhoneField.propTypes = {
  setValue: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  errors: PropTypes.shape({}),
  defaultValue: PropTypes.string,
  isIraAccount: PropTypes.bool,
  isPhoneConnectedTo2FA: PropTypes.bool,
  dataCy: PropTypes.string,
};

export default PhoneField;
